import { useEffect, useRef, useState } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { useChannelMessagingStore } from '../../Store/useChannelMessagingStore';
import { MessageContext } from './MessageContext';

export type ProductWindow = {
  origin: string;
  frameWindow: Window;
  id: string;
  acknowledged: boolean;
  logoutUrl: string;
  clientReady?: boolean;
};

export const messagingEnabled = (id: string, appList: any) => {
  if (!id || !appList || !Array.isArray(appList)) {
    return false; // app is not enabled if the list is empty or invalid
  }
  return appList.some((app: any) => app.appId === id);
}

export enum MESSAGES {
  CLIENT_READY = 'UNIFY_CLIENT_READY',
  INIT_PRODUCT = 'INIT_PRODUCT',
  INIT_ACKNOWLEDGE = 'INIT_ACKNOWLEDGE',
  LOGOUT = 'LOGOUT',
  CLOSE_APP = 'CLOSE_APP',
  WINDOW_OPEN = 'WINDOW_OPEN',
  UNIFY_PREFERENCE_CHANGED = 'UNIFY_PREFERENCE_CHANGED',
  NOTIFICATION_RECEIVED = 'NOTIFICATION_RECEIVED',
  UNIFY_TOKEN_REQUEST = 'UNIFY_TOKEN_REQUEST',
  UNIFY_TOKEN_RESPONSE = 'UNIFY_TOKEN_RESPONSE',
  APP_LOCATION_CHANGED = 'APP_LOCATION_CHANGED',
  RIGHT_CLICK = 'RIGHT_CLICK'
}

export enum LOGOUT_STATUS {
  LOGOUT_USERCONFIRM = 'LOGOUT_USERCONFIRM',
  LOGOUT_INIT = 'LOGOUT_INIT',
  LOGOUT_PROGRESS = 'LOGOUT_PROGRESS',
  APPS_COMPLETE = 'APPS_COMPLETE',
  NONE = 'NONE',
}

export enum SLO_MODE {
  MESSAGING = 'MESSAGING',
  END_POINTS = 'END_POINTS',
}

export const MessageProvider = (props: any) => {
  const unifyOrigin = document.URL;
  const [productWindows, setProductWindows] = useState<ProductWindow[]>([]);
  const [logoutStatus, setLogoutStatus] = useState(LOGOUT_STATUS.NONE);
  const [sloMode, setSloMode] = useState(SLO_MODE.MESSAGING);
  const [subscriberMap, setSubscriberMap] = useState<any>({});
  const [activeModel, setActiveModel] = useState<any>();
  const appDomains = useChannelMessagingStore((state:any) => state.appDomains);
  const { oktaAuth } = useOktaAuth() || {};
  const rightClickStack  = useChannelMessagingStore((state: any) => state.rightClickStack);
  const setRightClickStack = useChannelMessagingStore((state: any) => state.setRightClickStack);
  

  const notifyMessageRef = useRef<(msg: { action: string; payload: any }) => void>(() => {});
  // notifyMessageRef.current is set after notifyMessage is defined below.

  const initClient = (clientId:string) => {
      const clientApp = productWindows.find((prodWin) => prodWin.id === clientId);
      clientApp && (clientApp.clientReady = true);
      setProductWindows([ ...productWindows  ]);
  }

  const notifyMessage = (msg: { action: string, payload: any }) => {
    productWindows?.forEach((prodWin) => {
      const appOrigin = appDomains[prodWin.id!] || prodWin.origin;
        prodWin?.frameWindow?.postMessage(
          {
            id: prodWin.id,
            action: msg.action,
            payload: msg.payload,
            origin: unifyOrigin,
          },
          appOrigin
        );
      });
  }
  notifyMessageRef.current = notifyMessage;

  /***
   * Initializing the message listener from loaded apps, so far we are listening LOGOUT action,
   * and initiating the other applications to logout.
   */
  const messageHandler = (evt: MessageEvent) => {
    try {
      const { id, action, payload } = evt.data;
      if (id) {
        switch (action) {
          case MESSAGES.CLIENT_READY:
            initClient(id);
            handleDeepLink(evt);
            break;

          case MESSAGES.INIT_ACKNOWLEDGE:
            updateProductAcknowledge(id);
            break;

          case MESSAGES.LOGOUT:
            setLogoutStatus(LOGOUT_STATUS.LOGOUT_INIT);
            break;
          
          case MESSAGES.WINDOW_OPEN:
            openWindow(payload.url, id);
            break;

          case MESSAGES.UNIFY_TOKEN_REQUEST: {
            postTokenMessage(productWindows, id, appDomains, unifyOrigin);
            break;
          }

          case MESSAGES.APP_LOCATION_CHANGED: {
            handleDeepLink(evt);
            break;
          }

          case MESSAGES.RIGHT_CLICK:                  
            handleLatestRightClick(evt); 
          break;

          default:  
            notifyMessage({ action, payload });
            break;
        };
        // notify subscribers
        Object.keys(subscriberMap).map((key) => {  return subscriberMap[key](evt) });
      }
    }
    catch (err: any) {
      console.warn('Unify Messaging - messageHandler Error', err.stackTrace)
    }
  };
  
  // ─── Latest Right Click Handler ─────────────────────────────────────────────
    const handleLatestRightClick = (evt: MessageEvent): void => {
      const { origin, rcLink, id } = evt.data || {};
      try {
        // add to right click registry 
        const currentStack = useChannelMessagingStore.getState().rightClickStack;
        origin && setRightClickStack([...currentStack, {time: Date.now(), rcLink, origin, id}]);
      } catch (err: any) {
        console.warn('Unify Messaging - handleLatestRightClick Error', err.stackTrace);
      }
    };

  // ======================= VIS special case ======================

  const validateVISRoute = (route:string) => {
    try{
      let visURL = null;
      let rcUrl = '';
      try{ visURL = new URL(route); }catch(e){};
      if(visURL) {
        const rcLink = visURL?.origin + decodeURIComponent(route)?.split('redirectPath=')[1];
        let rcMatch = rightClickStack.find((rc:any) => rc.rcLink === rcLink);
        if(rcMatch) rcUrl = rcMatch?.rcLink;
      }
      return rcUrl;
    } catch(_){
      return '';
    }
  }

  // ===============================================================
  
  useEffect(() => {
     const unifyChannel = new BroadcastChannel('unify_bc');
    unifyChannel.onmessage = (event) => {
      try{ 
        const { id, action, origin, routeFrom} = event.data || {};
        if (id !== 'UNIFY_BC' || !action || origin !== window.location.origin) return;
        
        if(action === 'GET_LATEST_RIGHTCLICK') {
          const visLink = validateVISRoute(routeFrom);
          const rcMatch = rightClickStack.find((rc:any) => rc.rcLink === routeFrom || rc.rcLink === visLink);
        
          if(rcMatch) {
            // if request path and right clicks path matched
            unifyChannel.postMessage({ id: 'UNIFY_BC', action: 'LATEST_RIGHTCLICK', rcLink: rcMatch?.rcLink || visLink, origin: window.location.href, value: rcMatch }); 
          }
          let newRcStack = rightClickStack;
          // clear only one matched link, there might be multiple right clicks openes on same link
          const matchIndex = rightClickStack.findIndex((rc:any) => rc.rcLink === routeFrom || rc.rcLink === visLink );
          newRcStack = rightClickStack.filter((_: any, index: number) => index !== matchIndex);
          // clear registred right clicks happened beyoud 15 secs
          newRcStack = newRcStack.filter((rc:any) => (Date.now() - rc.time) < 20000); 
          setRightClickStack(newRcStack);
        }
       }catch(e){console.warn(e);}
    };
    return () => unifyChannel.close();
    // eslint-disable-next-line
  }, [rightClickStack]);

// ─── Broadcast Channel Messaging ──────────────────────────────────────────────

  
  //---------------------- deep share Link ------------------------

  const [contextUrl, setContextUrl] = useState<string>(window.location.href);
  const handleDeepLink = (evt: MessageEvent) => {
    const urlFromLocation = evt.data?.origin || evt.data?.value;
    const { id } = evt.data;
    if(!id) return; 
    if (typeof urlFromLocation === 'string' && urlFromLocation.length > 0) {
      setContextUrl(urlFromLocation);
      const activePane = JSON.parse(localStorage.getItem('activePane') || 'null');
      const activePaneLoaded = JSON.parse(localStorage.getItem('activePaneLoaded') || 'null');
      
      let linkPath = window.location.pathname;
      linkPath = window.location.pathname.indexOf('/dp/') >= 0 ? (window.location.pathname.split('/dp/')[0] || '') : linkPath;
      linkPath = window.location.pathname.indexOf('/bdp') >= 0 ? (window.location.pathname.split('/bdp')[0] || '') : linkPath;
      const paramHash = btoa(JSON.stringify({id, url: urlFromLocation }));
      setTimeout(function() {  
        if (activePane === id && activePaneLoaded === id) {
            window.history.replaceState({}, '', linkPath + '/bdp' + paramHash);
          }
        }, 50);
      }
  }

  const openWindow = (url:string, id:string) => {
    const exWindow = window.open(url, id, `left=0,top=200,width=${window.innerWidth},height=${window.innerHeight - 110}`);
    window.unifyClient = {
      modelCallBack: (data: string) => {
        console.log('Model URL', data);
        notifyMessage({ action: 'MODEL_REDIRECT', payload: { url: data }});
        closeWindow();
      },
      activeModel: exWindow
    }
    setActiveModel(exWindow);
  };

  const closeWindow = () => {
    activeModel?.close();
    window.unifyClient.activeModel.close();
    setActiveModel(null);
  }

  const updateProductAcknowledge = (id: string) => {
    const prodWIndow = productWindows.filter((prod) => prod.id === id)[0];
    prodWIndow && (prodWIndow.acknowledged = true);
  };

  /***
   * This method is called when app/workflow loaded into iFrame,
   * The frame information is stored into loaded frames list.
   * INIT message is sent to loaded app, and the loaded app will acknoledge with INIT_ACKNOWLEDGE
   * message,
   */
  const addProduct = (prod: ProductWindow) => {
    try {
      const { id, origin } = prod;
      const prodWindows = [...productWindows, prod];
      const prodWin = productWindows.filter((win) => win.id === id)[0];
      if (prodWin) {
        prodWin.origin = origin
      }
      else {
        setProductWindows(prodWindows);
      }

      /** 
      if (sloMode === SLO_MODE.MESSAGING) {
        prod?.frameWindow?.postMessage(
          { id, action: MESSAGES.INIT_PRODUCT, origin: unifyOrigin },
          origin
        );
      }
      */
    }
    catch (err: any) {
      console.warn('Unify Messaging - addProduct Error', err.stackTrace);
    }
  };

  const removeProduct = (prod: ProductWindow) => {
    const openedFrames = productWindows?.filter((win) => win.id !== prod.id);
    setProductWindows(openedFrames);
  };

  const messagingLogout = () => {
    /* if message is triggered by any loaded app, then exempt that app from sending LOGOUT message,
     *  because it's logout started already after its message to Unify.
     */
    try {
      notifyMessage({ action: MESSAGES.LOGOUT, payload: null });
      setLogoutStatus(LOGOUT_STATUS.LOGOUT_PROGRESS);
      setTimeout(
        () => setLogoutStatus(LOGOUT_STATUS.APPS_COMPLETE),
        productWindows.length > 0 ? 5000 : 0
      );
    }
    catch (err: any) {
      console.warn('Unify Messaging - messagingLogout', err.stackTrace)
    }
  };

  const endPointLogout = () => {
    try {
      const endPointsCount = productWindows.filter(
        (prod) => prod.logoutUrl && prod.logoutUrl !== ''
      ).length;
      setLogoutStatus(LOGOUT_STATUS.LOGOUT_PROGRESS);
      productWindows.forEach((product) => {
        product.logoutUrl &&
          ((document.getElementById(product.id) as HTMLIFrameElement).src =
            product.logoutUrl);
      });

      setTimeout(
        () => setLogoutStatus(LOGOUT_STATUS.APPS_COMPLETE),
        endPointsCount > 0 ? 5000 : 0
      );
    }
    catch (err: any) {
      console.warn('Unify Messaging - messagingLogout', err.stackTrace)
    }
  };

  /***
   * This method is invoked when loggout triggred and also  LOGOUT messsage is received from
   * any of the loaded app, so that all loaded apps informed to LOGOUT message to forse their logout process.
   */
  const logoutProducts = () => {
    sloMode === SLO_MODE.MESSAGING ? endPointLogout() : messagingLogout();
  };

  const subscribe = ({ key, listener }: { key: string, listener: Function }) => {
    setSubscriberMap({ ...subscriberMap, [key]: listener });
  }

  const unSubscribe = ({ key }: { key: string }) => {
    delete subscriberMap[key];
    setSubscriberMap({ ...subscriberMap });
  }

  useEffect(() => {
    const msgListener = function (evt: any) {
      messageHandler(evt)
    }
    if (sloMode === SLO_MODE.MESSAGING) {
      window.addEventListener('message', msgListener);
    }
    // Always return cleanup function to prevent listener leaks
    return () => {
      if (sloMode === SLO_MODE.MESSAGING) {
        window.removeEventListener('message', msgListener);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sloMode, productWindows]);


  useEffect(() => {
    const renewedHandler = (key: string, newToken: any) => {
      if (key === 'accessToken' && newToken?.accessToken) {

        const stored = readOktaTokenStorage()
        const renewedPayload = {
          accessToken: newToken,
          idToken: stored?.idToken,
          renewed: true,
        };
        notifyMessageRef.current({
          action: MESSAGES.UNIFY_TOKEN_RESPONSE,
          payload: renewedPayload,
        });
      }
    };

    oktaAuth?.tokenManager?.on('renewed', renewedHandler);

    return () => {
      oktaAuth?.tokenManager?.off('renewed', renewedHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oktaAuth]);

  return (
    <MessageContext.Provider
      value={{
        logoutProducts,
        addProduct,
        removeProduct,
        setLogoutStatus,
        setSloMode,
        logoutStatus,
        openedProducts: productWindows,
        subscribe,
        unSubscribe,
        notifyMessage,
        contextUrl
      }}
    >
      {props.children}
    </MessageContext.Provider>
  );
};

function postTokenMessage(productWindows: ProductWindow[], id: any, appDomains: any, unifyOrigin: string) {
  let stored: any = readOktaTokenStorage();
  const initialTokenPayload = {
    accessToken: stored?.accessToken,
    idToken: stored?.idToken,
  };
  const productWindow = productWindows.find((pw) => pw.id === id);
  if (productWindow) {
    const appOrigin = appDomains[id] || productWindow.origin;
    productWindow?.frameWindow?.postMessage(
      {
        id: productWindow.id,
        action: MESSAGES.UNIFY_TOKEN_RESPONSE,
        payload: initialTokenPayload,
        origin: unifyOrigin,
      },
      appOrigin
    );
  }
}

function readOktaTokenStorage() {
  const storedRaw = localStorage.getItem('okta-token-storage');
  let stored: any = {};
  try {
    stored = storedRaw ? JSON.parse(storedRaw) : {};
  } catch {
    stored = {};
  }
  return stored;
}